// v77.99.15 — useFeaturePageVariant hook
//
// Reads a UTM-style variant code from the URL and looks it up against
// `featurePageVariants.js`. Returns the merged hero copy (default fields
// overridden by any variant fields) plus the matched variant key so the
// caller can include it in analytics + the visible eyebrow.
//
// Lookup priority (first non-empty wins):
//   1. ?variant=<code>      — explicit override; easiest for QA
//   2. ?utm_content=<code>  — standard A/B variant slot in Google/Meta Ads
//   3. ?utm_campaign=<code> — fallback if the ads team prefers this slot
//
// Side effects: when a variant is shown, fires an analytics event
// `feature_variant_shown` with {page, variant} ONCE per mount. Pair with
// `feature_cta_click` (already fired by the Hero/FinalCTA components)
// to compute variant-level conversion rate.
//
// Why a hook and not a plain function: we need useEffect to fire the
// analytics event AFTER render, only once per visit, and only when the
// page actually presents a non-default variant.

import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import FEATURE_PAGE_VARIANTS from "@/lib/featurePageVariants";
import { trackEvent } from "@/lib/analytics";

// Allow only safe characters in variant codes — we put this on the page,
// so a malicious URL with HTML or whitespace can't slip into the DOM.
const SAFE_CODE = /^[a-zA-Z0-9_-]{1,64}$/;

function resolveVariantCode(searchString) {
  // useLocation().search includes the leading "?", URLSearchParams handles
  // that fine.
  const params = new URLSearchParams(searchString);
  for (const key of ["variant", "utm_content", "utm_campaign"]) {
    const raw = params.get(key);
    if (raw && SAFE_CODE.test(raw)) return raw;
  }
  return null;
}

/**
 * @param {string} pageKey         One of: "overview" | "calendar" | "expenses"
 *                                 | "messaging" | "court-bundle" | "journal"
 * @param {{ eyebrow:string, headline:string, body:string }} defaults
 *                                 The fallback hero copy from FeaturePages.jsx.
 *                                 Used as-is when no variant matches.
 * @returns {{ eyebrow:string, headline:string, body:string, variantKey:string|null }}
 */
export function useFeaturePageVariant(pageKey, defaults) {
  const location = useLocation();
  const code = useMemo(
    () => resolveVariantCode(location.search),
    [location.search],
  );

  // Resolve the override (or null) once per location/code change.
  const override = useMemo(() => {
    if (!code) return null;
    const pageVariants = FEATURE_PAGE_VARIANTS[pageKey];
    if (!pageVariants) return null;
    const match = pageVariants[code];
    return match || null;
  }, [pageKey, code]);

  // Fire analytics exactly once when a variant is presented. We tie the
  // effect to (pageKey, code, override) so it re-fires only if the URL
  // changes — e.g. user uses back/forward to swap variants — not on
  // every re-render of the page.
  useEffect(() => {
    if (override) {
      trackEvent("feature_variant_shown", {
        page: pageKey,
        variant: code,
      });
    }
  }, [pageKey, code, override]);

  return {
    eyebrow: override?.eyebrow || defaults.eyebrow,
    headline: override?.headline || defaults.headline,
    body: override?.body || defaults.body,
    variantKey: override ? code : null,
  };
}

export default useFeaturePageVariant;
